import {Trans} from '@lingui/macro';
import {compact} from 'lodash';
import React, {FC, ReactNode} from 'react';
import styled from 'styled-components';

import {buildInteractionIdProps} from '../../../../../../libs/shared/helpers/src/interaction/interactionHelpers';
import {useInitialEffect} from '../../../../../../libs/shared/helpers/src/react/hooks/useEffectHelpers';
import {Handler, HandlerOf} from '../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {AsyncStatusesEnum} from '../../../../../../libs/shared/helpers/src/types/asyncStatuses';
import {InteractionParent} from '../../../../../../libs/shared/ui/src/interactionTracking/interactionParent';
import {fontSizes, fontWeights} from '../../../../../../libs/shared/ui/src/styles/fontStyles';
import {AsyncData} from '../../../../../helpers/asyncDataHelpers';
import {ActivityModel} from '../../../../../models/api/activityModel';
import {AttachmentModel} from '../../../../../models/api/attachmentModel';
import {ChannelModel} from '../../../../../models/api/channelModel';
import {ConversationModel} from '../../../../../models/api/conversationModel';
import {InboxModel} from '../../../../../models/api/inboxModel';
import {SeenByModel} from '../../../../../models/api/seenByModel';
import {InterfaceEventsEnum, trackInterfaceEvent} from '../../../../../services/tracking';
import {FindTeammate} from '../../../../../store/data/teammates/lazyTeammatesSelectors';
import {ViewerPanelId} from '../../../../../store/panels/panelStateBase';
import {Accordion} from '../../../../common/accordion/accordion';
import {AccordionSectionData} from '../../../../common/accordion/accordionSection';
import {Dialog} from '../../../../common/dialogs/dialog';
import {DialogHeader} from '../../../../common/dialogs/dialogHeader';
import {ConversationSummaryAttachments} from './conversationSummaryAttachments';
import {ConversationSummaryDetails} from './conversationSummaryDetails';
import {ConversationSummaryPinnedActivitiesContainer} from './conversationSummaryPinnedActivitiesContainer';
import {ConversationSummarySectionsEnum} from './conversationSummarySections';
import {ConversationSummarySubject} from './conversationSummarySubject';

export interface ConversationSummaryProps {
  panelId: ViewerPanelId;
  conversation: ConversationModel;
  host: string;
  inboxes: ReadonlyArray<InboxModel>;
  channels: ReadonlyArray<ChannelModel>;
  findTeammate: FindTeammate;
  attachments: AsyncData<ReadonlyArray<AttachmentModel>>;
  seenBy: AsyncData<ReadonlyArray<SeenByModel>>;

  pinnedActivities: AsyncData<ReadonlyArray<ActivityModel>>;

  onAttachmentsRetryClick: Handler;
  onSeenByRetryClick: Handler;
  onPinnedActivitiesRetryClick: Handler;
  onRequestClose: Handler;

  initiallyExpandedSection?: ConversationSummarySectionsEnum;
  teammateId: number;
}

/*
 * Styles.
 */

const StyledContentDiv = styled.div`
  flex: auto;
  overflow: auto;
`;

const StyledCounterDiv = styled.div`
  font-size: ${fontSizes.veryTiny};
  font-weight: ${fontWeights.semibold};
  color: ${(p) => p.theme.greys.shade80};
  line-height: 16px;
  background: ${(p) => p.theme.greys.shade30};
  border-radius: 8px;
  height: 16px;
  min-width: 16px;
  text-align: center;
  padding: 0 4px;
`;

const StyledTitleWrapperDiv = styled.div`
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: auto;
  grid-column-gap: 7px;
  align-items: center;
  font-size: ${fontSizes.large};
`;

/*
 * Component.
 */

export const ConversationSummary: FC<ConversationSummaryProps> = (props) => {
  const {conversation, initiallyExpandedSection, onRequestClose, teammateId} = props;

  useInitialEffect(() => {
    fireConversationSummaryOpenedTrackingEvents(conversation, initiallyExpandedSection);
  });

  return (
    <Dialog
      width={460}
      minHeight="76%"
      maxHeight="76%"
      onRequestClose={onRequestClose}
      interactionId="conversationSummary"
    >
      <InteractionParent interactionId="conversation-summary">
        {renderConversationSummaryHeader(onRequestClose)}
        <StyledContentDiv>
          <ConversationSummarySubject conversation={conversation} teammateId={teammateId} />
          <Accordion allowMultiSelect sections={buildAccordionSectionsToRenderWithoutSeenBy(props)} />
        </StyledContentDiv>
      </InteractionParent>
    </Dialog>
  );
};

// When TvI FF is on, we want to deprecate the "Seen By" accordion section.
function buildAccordionSectionsToRenderWithoutSeenBy(
  props: ConversationSummaryProps,
): Array<AccordionSectionData> {
  return compact([
    buildPinnedActivitiesAccordionSection(props),
    buildAttachmentsAccordionSection(props),
    buildDetailsAccordionSection(props),
  ]);
}

function renderConversationSummaryHeader(onRequestClose: Handler) {
  return (
    <DialogHeader
      onRequestClose={onRequestClose}
      title={<Trans id="conversation-summary-title">About this conversation</Trans>}
    />
  );
}

/*
 * Pinned Activities section.
 */

function buildPinnedActivitiesAccordionSection(props: ConversationSummaryProps): AccordionSectionData {
  const {pinnedActivities, onPinnedActivitiesRetryClick, conversation, initiallyExpandedSection} = props;

  return {
    id: ConversationSummarySectionsEnum.PINNED_ACTIVITIES,
    title: renderPinnedActivitiesTitle(pinnedActivities),
    isExpandedByDefault: initiallyExpandedSection === ConversationSummarySectionsEnum.PINNED_ACTIVITIES,
    onSectionToggled: makeOnSectionToggled(
      ConversationSummarySectionsEnum.PINNED_ACTIVITIES,
      conversation.id,
    ),
    sectionPadding: 16,
    content: (
      <ConversationSummaryPinnedActivitiesContainer
        pinnedActivities={pinnedActivities}
        onPinnedActivitiesRetryClick={onPinnedActivitiesRetryClick}
        onRequestClose={props.onRequestClose}
        panelId={props.panelId}
      />
    ),
  };
}

function renderPinnedActivitiesTitle(pinnedActivities: AsyncData<ReadonlyArray<ActivityModel>>): ReactNode {
  const numAttachmentsLabel =
    pinnedActivities.status === AsyncStatusesEnum.LOADED ? (
      <StyledCounterDiv {...buildInteractionIdProps('conversation-summary-pinned-count')}>
        {pinnedActivities.data.length}
      </StyledCounterDiv>
    ) : (
      ''
    );

  return (
    <StyledTitleWrapperDiv>
      <Trans id="conversation-summary-pinned-activities">Pinned comments</Trans>
      {numAttachmentsLabel}
    </StyledTitleWrapperDiv>
  );
}

/*
 * Attachments section.
 */

function buildAttachmentsAccordionSection(props: ConversationSummaryProps): AccordionSectionData {
  const {attachments, onAttachmentsRetryClick, conversation, initiallyExpandedSection} = props;

  return {
    id: ConversationSummarySectionsEnum.ATTACHMENTS,
    title: renderAttachmentsTitle(attachments),
    isExpandedByDefault: initiallyExpandedSection === ConversationSummarySectionsEnum.ATTACHMENTS,
    onSectionToggled: makeOnSectionToggled(ConversationSummarySectionsEnum.ATTACHMENTS, conversation.id),
    sectionPadding: 16,
    content: (
      <ConversationSummaryAttachments
        attachments={attachments}
        onAttachmentsRetryClick={onAttachmentsRetryClick}
      />
    ),
  };
}

function renderAttachmentsTitle(attachments: AsyncData<ReadonlyArray<AttachmentModel>>): ReactNode {
  const numAttachmentsLabel =
    attachments.status === AsyncStatusesEnum.LOADED ? (
      <StyledCounterDiv {...buildInteractionIdProps('conversation-summary-attachment-count')}>
        {attachments.data.length}
      </StyledCounterDiv>
    ) : (
      ''
    );

  return (
    <StyledTitleWrapperDiv>
      <Trans id="conversation-summary-attachments">Attachments</Trans>
      {numAttachmentsLabel}
    </StyledTitleWrapperDiv>
  );
}

/*
 * Details Section.
 */

function buildDetailsAccordionSection(props: ConversationSummaryProps): AccordionSectionData {
  const {conversation, host, inboxes, channels, initiallyExpandedSection} = props;

  return {
    id: ConversationSummarySectionsEnum.DETAILS,
    title: (
      <StyledTitleWrapperDiv>
        <Trans id="conversation-summary-details">Details</Trans>
      </StyledTitleWrapperDiv>
    ),
    isExpandedByDefault: initiallyExpandedSection === ConversationSummarySectionsEnum.DETAILS,
    onSectionToggled: makeOnSectionToggled(ConversationSummarySectionsEnum.DETAILS, conversation.id),
    content: (
      <ConversationSummaryDetails
        conversation={conversation}
        host={host}
        inboxes={inboxes}
        channels={channels}
        safetyNets={conversation.safetyNets ?? []}
      />
    ),
  };
}

/*
 * Tracking.
 */

function fireConversationSummaryOpenedTrackingEvents(
  conversation: ConversationModel,
  initiallyExpandedSection?: ConversationSummarySectionsEnum,
) {
  trackInterfaceEvent(InterfaceEventsEnum.CONVERSATION_SUMMARY_SUMMARY_VIEW, {
    conversation_id: conversation.id,
  });

  if (initiallyExpandedSection) {
    trackConversationSummarySectionViewed(initiallyExpandedSection, conversation.id);
  }
}

function trackConversationSummarySectionViewed(
  section: ConversationSummarySectionsEnum,
  conversationId: number,
) {
  trackInterfaceEvent(InterfaceEventsEnum.CONVERSATION_SUMMARY_SECTION_VIEW, {
    conversation_id: conversationId,
    section_type: sectionToTrackingFormat(section),
  });
}

function sectionToTrackingFormat(section: ConversationSummarySectionsEnum): string {
  switch (section) {
    case ConversationSummarySectionsEnum.PINNED_ACTIVITIES:
      return 'pinned_activities';
    case ConversationSummarySectionsEnum.ATTACHMENTS:
      return 'attachments';
    case ConversationSummarySectionsEnum.DETAILS:
      return 'details';

    default:
      throw new Error(`Unexpected conversation summary section: ${section}`);
  }
}

/*
 * Helpers.
 */

function makeOnSectionToggled(
  section: ConversationSummarySectionsEnum,
  conversationId: number,
): HandlerOf<boolean> {
  return (wasExpanded: boolean) =>
    wasExpanded && trackConversationSummarySectionViewed(section, conversationId);
}
